import { Box } from '@mui/material';
import dayjs, { Dayjs, OpUnitType } from 'dayjs';
import { memo, useEffect, useState } from 'react';
import Timeline, { CustomHeader, TimelineGroupBase, TimelineHeaders, TimelineItemBase } from 'react-calendar-timeline';
import 'react-calendar-timeline/lib/Timeline.css';
import classNames from 'classnames/bind';
import styles from './BaseTimeline.module.scss';
import BaseScrollbar from '../base-scrollbar/BaseScrollbar';
import { ReactCalendarItemRendererProps } from 'react-calendar-timeline';

export type BaseTimelineProps<T> = {
  currentTime?: Date | Dayjs;
  items: Array<BaseTimelineDayItem<T>>;
  groups: Array<TimelineGroupBase>;
  unit?: string;
  itemRenderer?: (item: BaseTimelineDayItem<T>) => React.ReactNode;
};

const cx = classNames.bind(styles);

export type BaseTimelineDayItem<TItem> = {
  resource: Array<TItem>;
} & TimelineItemBase<Date>;

const BaseTimeline = <T extends Record<any, any>>(props: BaseTimelineProps<T>) => {
  //#region Destructuring Props
  const { currentTime = dayjs(), unit = 'month' } = props;

  //#endregion Destructuring Props

  //#region Declare Hook
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [startTime, setStartTime] = useState<Date>();
  const [endTime, setEndTime] = useState<Date>();
  //#endregion Declare State
  const [items, setItems] = useState<Array<BaseTimelineDayItem<T>>>([]);
  const [key, setKey] = useState<number>();

  useEffect(() => {
    setItems(props.items as any as Array<BaseTimelineDayItem<T>>);
    setKey(Date.now());
  }, [props.items]);

  //#region Implement Hook
  useEffect(() => {
    setStartTime(
      dayjs(currentTime)
        .startOf(unit as OpUnitType)
        .toDate()
    );
    setEndTime(
      dayjs(currentTime)
        .endOf(unit as OpUnitType)
        .toDate()
    );
  }, [currentTime, unit]);
  //#endregion Implement Hook

  //#region Handle Function
  //#endregion Handle Function

  const itemRenderer = (calendarProps: ReactCalendarItemRendererProps<BaseTimelineDayItem<T>>) => {
    return (
      <Box {...calendarProps.getItemProps({})} key={calendarProps?.item?.id}>
        {props?.itemRenderer
          ? props?.itemRenderer(calendarProps.item)
          : calendarProps?.item?.resource.map(({ title }) => title).join(', ')}
      </Box>
    );
  };

  return (
    startTime &&
    endTime && (
      <BaseScrollbar className={cx('base-timeline-container')}>
        <Timeline
          groups={props.groups}
          items={items}
          defaultTimeStart={startTime}
          defaultTimeEnd={endTime}
          visibleTimeStart={startTime.valueOf()}
          visibleTimeEnd={endTime.valueOf()}
          sidebarWidth={0}
          className={cx('base-timeline')}
          key={key}
          itemRenderer={itemRenderer}
        >
          <TimelineHeaders>
            <CustomHeader>
              {(options: any) => {
                return (
                  <Box {...options.getRootProps()}>
                    {options.headerContext.intervals.map((interval: any) => {
                      return (
                        <Box
                          className='rct-dateHeader'
                          {...options.getIntervalProps({
                            interval,
                          })}
                          key={interval.startTime.format('YYYY-MM-DD')}
                        >
                          <Box className='flex flex-row'>
                            <Box className='text-[#BCB8DC] mr-[4px]'>
                              {interval.startTime.format('dd').substring(0, 1)}
                            </Box>
                            <Box className='text-[#124378]'>{interval.startTime.format('DD')}</Box>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                );
              }}
            </CustomHeader>
          </TimelineHeaders>
        </Timeline>
      </BaseScrollbar>
    )
  );
};

export default memo(BaseTimeline) as typeof BaseTimeline;
